"use client";

import { TypeHero, TypeHeroFields, TypeImageSkeleton } from "@contentful/types";
import { Button } from "@yardzen/ui/components/button";
import { ContentfulText } from "@yardzen/ui/components/contentful-text";
import { H3 } from "@yardzen/ui/components/h3";
import { H4 } from "@yardzen/ui/components/h4";
import { Link } from "@yardzen/ui/components/link";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import { Entry, EntryFieldTypes } from "contentful";
import Image from "next/image";
import { ListItemCollection } from "./ListItemCollection";
import { StarRating } from "./StarRating";
import { Badge } from "@yardzen/ui/components/badge";

type ExtractSymbol<T> = T extends EntryFieldTypes.Symbol<infer U> ? U : never;

export function HeroShowcase({
  hero,
}: {
  hero: TypeHero<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const variant = hero?.fields.variant;
  const background = hero.fields.background || "none";
  const paddingTopResolved = hero?.fields.paddingAbove ?? "default";

  return (
    <div
      className={cn(
        "hero-showcase flex flex-col items-center justify-center px-1 max-md:pb-4 md:px-8",
      )}
    >
      {hero.fields.customStyles && (
        <style
          // NOTE: customStyles is expected to be valid CSS text coming from Contentful.
          // Author it with selectors scoped to `.hero-showcase` to avoid global leaks.
          dangerouslySetInnerHTML={{ __html: hero.fields.customStyles }}
        />
      )}
      <div
        className={cn(
          "w-full max-w-7xl rounded-lg bg-opacity-50 max-md:max-w-full",
          heroVariants({ backGroundPadding: paddingTopResolved, background }),
          hero.fields.internalYardzenId &&
            `hero-${hero.fields.internalYardzenId}`,
        )}
      >
        <div
          className={cn(
            "grid grid-cols-1 justify-center",
            heroVariants({ variant }),
          )}
        >
          <TextCard fields={hero.fields} variant={variant} />
          {hero?.fields.images?.at(0)?.fields.link ? (
            <Link href={hero?.fields.images?.at(0)?.fields.link}>
              <ImageCard
                image={hero?.fields.images?.at(0)}
                mobileImage={hero?.fields.mobileOnlyImages?.at(0)}
                variant={variant}
                imageAspect={hero.fields.imageAspect}
              />
            </Link>
          ) : (
            <ImageCard
              image={hero?.fields.images?.at(0)}
              mobileImage={hero?.fields.mobileOnlyImages?.at(0)}
              variant={variant}
              imageAspect={hero.fields.imageAspect}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const heroVariants = cva("", {
  variants: {
    variant: {
      background: "grid-cols-1 gap-8",
      "showcase-large": "grid-cols-1 gap-8",
      "showcase-medium": "grid-cols-1 gap-8 max-md:px-4",
      slider: "grid-cols-1 gap-8",
      "hero-left-split":
        "md:flex md:flex-row-reverse md:min-h-[520px] gap-8 md:gap-28 max-md:px-4 md:[&>*]:basis-1/2",
      "hero-right-split":
        "md:flex md:flex-row md:min-h-[520px] gap-8 md:gap-28 max-md:px-4 md:[&>*]:basis-1/2",
      values: "grid-cols-1 gap-8",
      grid: "grid-cols-1 gap-8",
      about: "grid-cols-1 gap-8",
      "showcase-background": "",
    },
    background: {
      "moss-green":
        "py-8 md:px-8 bg-brand-moss-greens-10 max-md:mt-12 md:mt-12",
      none: "",
    },
    backGroundPadding: {
      default: "py-4 md:py-8 mt-8 md:mt-10",
      small: "py-4 md:py-8 mt-4 pt-2 md:pt-4 mt-2",
    },
  },
});

const textVariants = cva("", {
  variants: {
    variant: {
      background: "text-scheme-text-primary text-4xl",
      "showcase-large": "text-scheme-text-primary text-6xl max-md:px-4",
      "showcase-medium":
        "text-scheme-text-primary text-3xl lg:text-4xl text-center",
      slider: "text-scheme-text-primary text-4xl",
      // The two split variants carry no size: `textSize` owns it for them, so
      // an editor can actually make a hero title bigger. Every other variant
      // still sizes itself here.
      "hero-left-split": "text-scheme-text-primary",
      "hero-right-split": "text-scheme-text-primary",
      values: "text-scheme-text-primary text-4xl",
      grid: "text-scheme-text-primary text-4xl",
      about: "text-scheme-text-primary text-4xl",
      "showcase-background": "",
    },
    descriptionVariant: {
      background: "text-scheme-text-primary text-base",
      "showcase-large": "text-scheme-text-primary text-base max-md:px-4",
      "showcase-medium": "text-scheme-text-primary text-base",
      slider: "text-scheme-text-primary text-base",
      "hero-left-split":
        "text-scheme-text-primary text-base md:text-lg text-center",
      "hero-right-split":
        "text-scheme-text-primary text-base md:text-lg text-center",
      values: "text-scheme-text-primary text-4xl",
      grid: "text-scheme-text-primary text-4xl",
      about: "text-scheme-text-primary text-4xl",
      "showcase-background": "",
    },
    background: {
      "moss-green": "",
      none: "",
    },
    // A named scale rather than "large means whatever the variant decided".
    // Sizes only reach the split variants; the rest set their own above and
    // resolve `textSize` to undefined, so nothing else moves.
    textSize: {
      small: "text-2xl md:text-3xl",
      medium: "text-3xl md:text-4xl",
      large: "text-4xl md:text-5xl",
      "x-large": "text-5xl md:text-6xl",
    },
  },
  compoundVariants: [
    {
      variant: ["hero-left-split", "hero-right-split"],
      background: "moss-green",
      class: "text-scheme-text-secondary",
    },
  ],
});

const SPLIT_VARIANTS = ["hero-left-split", "hero-right-split"] as const;

function isSplitVariant(
  variant: ExtractSymbol<TypeHeroFields["variant"]>,
): boolean {
  return (SPLIT_VARIANTS as readonly string[]).includes(variant);
}

const imageVariants = cva("relative m-auto w-full md:h-full", {
  variants: {
    variant: {
      background: "w-full",
      "showcase-large": "",
      "showcase-medium": "md:w-10/12",
      slider: "w-full",
      // No negative margin here. The image fills its half of the split, which
      // is what the pull-to-the-viewport-edge was reaching for and missing.
      "hero-left-split": "",
      "hero-right-split": "",
      values: "",
      grid: "",
      about: "",
      "showcase-background": "",
    },
    image: {
      background: "",
      "showcase-large": "w-full",
      "showcase-medium": "w-full",
      slider: "",
      "hero-left-split": "h-full rounded-lg",
      "hero-right-split": "h-full rounded-lg",
      values: "",
      grid: "",
      about: "",
      "showcase-background": "",
    },
  },
});

/**
 * The shape the image is cropped into on the split variants. `original` is the
 * escape hatch: no frame, the file keeps its own proportions, which is what a
 * diagram or a logo needs. Anything else crops with `object-cover`.
 */
const aspectVariants = cva("", {
  variants: {
    imageAspect: {
      landscape: "aspect-[4/3]",
      square: "aspect-square",
      vertical: "aspect-[3/4]",
      original: "",
    },
  },
});

function ImageCard({
  image,
  mobileImage,
  variant,
  imageAspect,
}: {
  image?: Entry<TypeImageSkeleton, "WITHOUT_UNRESOLVABLE_LINKS">;
  mobileImage?: Entry<TypeImageSkeleton, "WITHOUT_UNRESOLVABLE_LINKS">;
  variant: ExtractSymbol<TypeHeroFields["variant"]>;
  imageAspect?: ExtractSymbol<TypeHeroFields["imageAspect"]>;
}) {
  if (!image) return null;

  // Only the splits take an aspect, and on a split there is no such thing as
  // "unset": an empty field lands on `landscape` rather than the old behaviour
  // of stretching to the card's height, which is what made uploads unusable.
  // Every split image is one of five named shapes now, old entries included.
  const aspect = isSplitVariant(variant)
    ? (imageAspect ?? "landscape")
    : undefined;
  const isOriginal = aspect === "original";
  // The base carries `md:h-full`, which takes its height from the parent and
  // so beats `aspect-ratio`. Hand the height back to the aspect. `cn` is
  // tailwind-merge, so this last class is the one that survives.
  const frameClass =
    aspect && !isOriginal
      ? cn(aspectVariants({ imageAspect: aspect }), "md:h-auto")
      : "";
  const fitClass = isOriginal ? "h-auto w-full object-contain" : "object-cover";

  return (
    <div className={cn(imageVariants({ variant }), frameClass)}>
      <Image
        src={`https:${image?.fields.image?.fields.file?.url}`}
        alt={image?.fields.image?.fields.description ?? ""}
        height={600}
        width={isSplitVariant(variant) ? 600 : 1280}
        className={cn(
          fitClass,
          imageVariants({ image: variant }),
          aspect && !isOriginal && "h-full w-full",
          mobileImage ? "hidden md:block" : "",
        )}
      />
      {mobileImage && (
        <Image
          src={`https:${mobileImage?.fields.image?.fields.file?.url}`}
          alt={mobileImage?.fields.image?.fields.description ?? ""}
          height={600}
          width={600}
          className={cn(
            fitClass,
            imageVariants({ image: variant }),
            aspect && !isOriginal && "h-full w-full",
            mobileImage ? "block md:hidden" : "",
          )}
        />
      )}
    </div>
  );
}

function TextCard({
  fields,
  variant,
}: {
  fields: TypeHero<"WITHOUT_UNRESOLVABLE_LINKS">["fields"];
  variant: ExtractSymbol<TypeHeroFields["variant"]>;
}) {
  const listItemCount = fields.listItemCollection?.fields.listItems?.length;
  const ctaUrl = fields.cta?.fields.url;
  const isShowCaseLg = variant === "showcase-large";
  const isLeftRightBG =
    (variant === "hero-left-split" || variant === "hero-right-split") &&
    fields.background === "moss-green";
  const isLeftRightNoBG =
    (variant === "hero-left-split" || variant === "hero-right-split") &&
    fields.background !== "moss-green";

  // Only the split variants take a size from `textSize`; the others size
  // themselves in `textVariants.variant` and must resolve to undefined here or
  // the scale would fight them. When an editor has not picked a size, fall back
  // to what that combination already rendered — moss-green splits were a step
  // larger — so existing entries are untouched.
  const textSizeResolved = isSplitVariant(variant)
    ? (fields.textSize ?? (isLeftRightBG ? "large" : "medium"))
    : undefined;
  const textAlignClass = isLeftRightNoBG
    ? "text-center md:text-left"
    : isLeftRightBG
      ? "text-left"
      : "";

  const eyebrowImageUrl = fields.eyebrowImage?.fields?.file?.url
    ? `https:${fields.eyebrowImage.fields.file.url}`
    : null;

  const eyebrowImageAlt =
    fields.eyebrowImage?.fields?.description ||
    fields.eyebrowImage?.fields?.title ||
    "";

  const showTitle =
    fields.title && !fields.displayOptions?.includes("suppress-title");

  const hasCTAs = fields.CTAs && fields.CTAs.length > 0;

  const hasHeaderBlock = Boolean(
    (fields.title && !fields.displayOptions?.includes("suppress-title")) ||
      fields.starRating ||
      fields.eyebrow ||
      fields.eyebrowImage ||
      fields.subtitle ||
      fields.description ||
      fields.listItemCollection ||
      fields.cta ||
      fields.CTAs?.length,
  );

  return hasHeaderBlock ? (
    <div className={cn("flex flex-col items-center justify-center")}>
      <div
        className={cn(
          textAlignClass,
          isLeftRightBG ? "lg:w-5/6" : "",
          isShowCaseLg ? "w-full" : "",
        )}
      >
        {fields.eyebrow && <Badge variant="zest">{fields.eyebrow}</Badge>}

        {eyebrowImageUrl && (
          <div
            className={cn(
              "mb-6 mt-3 flex",
              textAlignClass
                ? "justify-center md:justify-start"
                : "justify-center",
            )}
          >
            <Image
              src={eyebrowImageUrl}
              alt={eyebrowImageAlt}
              width={800}
              height={200}
              style={{
                maxWidth: `min(${fields.eyebrowImageWidth ?? 220}px, 100%)`,
              }}
              className="h-auto w-auto"
              priority
            />
          </div>
        )}

        {(showTitle || fields.subtitle) && (
          <div
            className={cn(
              "mb-1 px-0 pb-8 md:pb-0",
              isShowCaseLg
                ? "justify-left grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-8"
                : "",
            )}
          >
            {showTitle && (
              <H3
                className={cn(
                  textVariants({
                    variant,
                    background: fields.background,
                    textSize: textSizeResolved,
                  }),
                  textAlignClass,
                )}
              >
                {fields.title}
              </H3>
            )}
            {fields.subtitle && (
              <H4
                className={cn(
                  "text-scheme-text-secondary text-2xl max-md:px-4 md:text-3xl",
                  textAlignClass,
                )}
              >
                {fields.subtitle}
              </H4>
            )}
          </div>
        )}
        {fields.description && (
          <ContentfulText
            content={fields.description}
            pClassName={cn(
              "whitespace-pre-line pt-4 text-lg",
              textVariants({ descriptionVariant: variant }),
              textAlignClass,
            )}
            ulClassName={cn(
              cn(
                textVariants({ descriptionVariant: variant }),
                "ml-4 list-disc text-left",
              ),
            )}
          />
        )}
        {fields.listItemCollection && (
          <div className="mt-2">
            <ListItemCollection
              listItemCollection={fields.listItemCollection}
              listItemsClassName={
                listItemCount && listItemCount > 3
                  ? "grid-cols-2 gap-3"
                  : "grid-cols-1 gap-3"
              }
              listItemHeadingClassName="font-display text-xl font-normal mb-1"
              listItemSubHeadingClassName="text-base text-brand-moss-greens-90"
              listItemIconClassName="text-brand-greens-light h-[16px] w-[16px]"
              listItemIconWrapperClassName="w-7 h-7 px-1.5 pb-1 pt-1"
              iconSize="sm"
            />
          </div>
        )}
        {fields.CTAs?.length ? (
          <div className="mt-3 flex flex-col gap-3 px-0 pb-2 md:flex-row md:pt-6">
            {fields.CTAs.map((cta) => {
              if (!cta) return null;
              return (
                <Button
                  key={cta.sys.id}
                  className="mx-2 w-full md:w-auto"
                  variant={cta.fields.variant ?? "default"}
                  asChild
                >
                  <Link
                    className="m-0 whitespace-nowrap font-medium"
                    href={cta.fields.url}
                    target={cta.fields.openInNewTab ? "_blank" : undefined}
                    rel={
                      cta.fields.openInNewTab
                        ? "noopener noreferrer"
                        : undefined
                    }
                  >
                    {cta.fields.buttonText}
                  </Link>
                </Button>
              );
            })}
          </div>
        ) : fields.cta ? (
          <div className="mt-3 px-0 pb-2 md:pt-6">
            <Button
              className="mx-2 w-full"
              variant={fields.cta?.fields.variant ?? "default"}
              asChild
            >
              <Link
                className="m-0 hidden whitespace-nowrap font-medium max-[429px]:py-3 max-[390px]:px-3 max-[365px]:whitespace-normal max-[365px]:px-6 max-[365px]:leading-3 min-[360px]:inline-flex"
                href={ctaUrl}
                target={fields.cta?.fields.openInNewTab ? "_blank" : undefined}
                rel={
                  fields.cta?.fields.openInNewTab
                    ? "noopener noreferrer"
                    : undefined
                }
              >
                {fields.cta?.fields.buttonText}
              </Link>
            </Button>
          </div>
        ) : null}
        {fields.starRating && (
          <div className={cn("mt-4", textAlignClass)}>
            <StarRating
              starRating={fields.starRating}
              variant={
                isLeftRightNoBG || isLeftRightBG
                  ? "left-aligned"
                  : "left-aligned-small"
              }
              containerClassName={
                isLeftRightNoBG
                  ? "inline-flex items-center gap-2"
                  : isLeftRightBG
                    ? ""
                    : undefined
              }
              textColorClass={
                isLeftRightNoBG || isLeftRightBG
                  ? "text-scheme-text-secondary"
                  : fields.textColor === "white"
                    ? "text-white"
                    : undefined
              }
            />
          </div>
        )}
      </div>
    </div>
  ) : (
    <></>
  );
}
